import { useCallback, useMemo, useContext } from 'react';
import { LogMetadata } from '../utils/logger';
import { LoggerContext } from '../contexts/LoggerContext';
import { useToast } from './useToast';

export interface UseLoggerOptions {
  context?: string;
  enableToastIntegration?: boolean;
}

export interface LoggerWithToast {
  // Core Logger methods
  debug: (message: string, metadata?: LogMetadata) => void;
  info: (message: string, metadata?: LogMetadata) => void;
  warn: (message: string, metadata?: LogMetadata, error?: Error) => void;
  error: (message: string, metadata?: LogMetadata, error?: Error) => void;
  userAction: (action: string, metadata?: LogMetadata) => void;
  performance: (
    operation: string,
    duration: number,
    metadata?: LogMetadata
  ) => void;
  time: (operation: string) => () => void;

  // Toast integration methods
  errorWithToast: (
    message: string,
    toastMessage?: string,
    metadata?: LogMetadata,
    error?: Error
  ) => void;
  warnWithToast: (
    message: string,
    toastMessage?: string,
    metadata?: LogMetadata,
    error?: Error
  ) => void;
  infoWithToast: (
    message: string,
    toastMessage?: string,
    metadata?: LogMetadata
  ) => void;
  successWithToast: (
    message: string,
    toastMessage?: string,
    metadata?: LogMetadata
  ) => void;
}

export const useLogger = (
  contextOrOptions?: string | UseLoggerOptions
): LoggerWithToast => {
  const loggerContext = useContext(LoggerContext);
  const toast = useToast();

  if (!loggerContext) {
    throw new Error('useLogger must be used within a LoggerProvider');
  }

  const options = useMemo(() => {
    if (typeof contextOrOptions === 'string') {
      return { context: contextOrOptions, enableToastIntegration: true };
    }
    return { enableToastIntegration: true, ...contextOrOptions };
  }, [contextOrOptions]);

  const logger = useMemo(() => {
    if (options.context) {
      return loggerContext.logger.createChildLogger(options.context);
    }
    return loggerContext.logger;
  }, [loggerContext.logger, options.context]);

  const errorWithToast = useCallback(
    (
      message: string,
      toastMessage?: string,
      metadata?: LogMetadata,
      error?: Error
    ) => {
      logger.error(message, metadata, error);
      if (options.enableToastIntegration) {
        toast.showError('Error', toastMessage || message);
      }
    },
    [logger, toast, options.enableToastIntegration]
  );

  const warnWithToast = useCallback(
    (
      message: string,
      toastMessage?: string,
      metadata?: LogMetadata,
      error?: Error
    ) => {
      logger.warn(message, metadata, error);
      if (options.enableToastIntegration) {
        toast.showWarning('Warning', toastMessage || message);
      }
    },
    [logger, toast, options.enableToastIntegration]
  );

  const infoWithToast = useCallback(
    (message: string, toastMessage?: string, metadata?: LogMetadata) => {
      logger.info(message, metadata);
      if (options.enableToastIntegration) {
        toast.showInfo('Info', toastMessage || message);
      }
    },
    [logger, toast, options.enableToastIntegration]
  );

  const successWithToast = useCallback(
    (message: string, toastMessage?: string, metadata?: LogMetadata) => {
      logger.info(message, metadata);
      if (options.enableToastIntegration) {
        toast.showSuccess('Success', toastMessage || message);
      }
    },
    [logger, toast, options.enableToastIntegration]
  );

  return useMemo(
    () => ({
      // Core logger methods
      debug: logger.debug.bind(logger),
      info: logger.info.bind(logger),
      warn: logger.warn.bind(logger),
      error: logger.error.bind(logger),
      userAction: logger.userAction.bind(logger),
      performance: logger.performance.bind(logger),
      time: logger.time.bind(logger),

      // Toast integration methods
      errorWithToast,
      warnWithToast,
      infoWithToast,
      successWithToast,
    }),
    [logger, errorWithToast, warnWithToast, infoWithToast, successWithToast]
  );
};
